import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useUpsertPromotionForm } from '../hooks/useUpsertPromotionForm';
import type { UpsertPromotionDto } from '../types/PromotionDto';
import { UpsertPromotionForm } from './UpsertPromotionForm';
import { Title } from '../../../components/Title';

interface NewPromotionModalProps {
	close: () => void;
	isOpened: boolean;
	triggerListUpdate: () => void;
}

export function NewPromotionModal({
	isOpened,
	close,
	triggerListUpdate,
}: NewPromotionModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertPromotionForm();

	const newPromotionMutation = mutate<UpsertPromotionDto>(
		`${organisation}/promotions/new`,
		inputs,
		'multipart/form-data',
	);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		triggerListUpdate();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'New Promotion'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={newPromotionMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Promotion Added!'}
					body={'A new promotion has been added. Click Ok to continue...'}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newPromotionMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertPromotionForm form={form} />
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
